import { DefinedPropertyObject, IEntityMinimalModel } from "../../api/GenericTypes";
import {
  TabStoreFilterSidebarProps,
  TabStoreFilterFormProvider,
} from "../../common/sidebar/filtersidebar/TabStoreFilterFormProvider";
import { EntityFilterSideBar } from "../../common/sidebar/filtersidebar/EntityFilterBar";
import { InventoryItem, InventoryItemFilters } from "../../api/Inventories";
import { CustomTypesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/CustomTypesVirtualizedSelectForm";
import { InventoryItemsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/InventoriesVirtualizedSelectForm";
import { ProjectsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import {
  CustomFieldFilterLayout,
  CustomFieldFilterLayoutForm,
} from "../../Customization/CustomFieldFilters/CustomFieldFilterLayout/CustomFieldFilterLayout";
import {
  CombinedModificationRecordEntityDefaultFormValues,
  combinedModificationRecordEntityFiltersToForm,
  combinedModificationRecordEntityFormToFilters,
  ICombinedModificationRecordEntityForm,
} from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterFormModels";
import {
  ILockableEntityForm,
  LockableEntityDefaultFilterFormValues,
  lockableEntityFiltersToForm,
  lockableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/LockableEntityFilterFormModels";
import {
  ISignableEntityForm,
  SignableEntityDefaultFilterFormValues,
  signableEntityFiltersToForm,
  signableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterFormModels";
import {
  ISoftDeletableEntityForm,
  SoftDeletableEntityDefaultFilterFormValues,
  softDeletableEntityFiltersToForm,
  softDeletableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/SoftDeletableEntityFilterFormModels";
import { CombinedModificationRecordEntityFilterForm } from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterForm";
import { SignableEntityFilterForm } from "../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterForm";
import { defaultInventoryItemsFilters } from "./InventoryItemsTable";
import { FoldableFilters } from "../../common/sidebar/filtersidebar/GenericFilters/common/FoldableFilters";

export interface InventoryItemFilterForm
  extends ICombinedModificationRecordEntityForm,
    ILockableEntityForm,
    ISignableEntityForm,
    ISoftDeletableEntityForm,
    CustomFieldFilterLayoutForm {
  customTypeIds?: IEntityMinimalModel[] | undefined | null;
  childrenOfParentIds?: IEntityMinimalModel[] | undefined | null;
  descendantsOfIds?: IEntityMinimalModel[] | undefined | null;
  projectIds?: IEntityMinimalModel[] | undefined | null;
}
const defaultValues: DefinedPropertyObject<InventoryItemFilterForm> = {
  customTypeIds: null,
  childrenOfParentIds: null,
  descendantsOfIds: null,
  projectIds: null,
  ...CombinedModificationRecordEntityDefaultFormValues,
  ...LockableEntityDefaultFilterFormValues,
  ...SignableEntityDefaultFilterFormValues,
  ...SoftDeletableEntityDefaultFilterFormValues,
  customFieldFilters: [],
};
export const inventoryItemsFormToFilters = (form: InventoryItemFilterForm): InventoryItemFilters => {
  let filters: InventoryItemFilters = {
    ...combinedModificationRecordEntityFormToFilters(form),
    ...lockableEntityFormToFilters(form),
    ...signableEntityFormToFilters(form),
    ...softDeletableEntityFormToFilters(form),
  };
  filters.customTypeIds =
    Array.isArray(form.customTypeIds) && form.customTypeIds.length > 0 ? form.customTypeIds?.map((d) => d.id) : null;
  filters.childrenOfParentIds =
    Array.isArray(form.childrenOfParentIds) && form.childrenOfParentIds.length > 0
      ? form.childrenOfParentIds?.map((d) => d.id)
      : null;
  filters.descendantsOfIds =
    Array.isArray(form.descendantsOfIds) && form.descendantsOfIds.length > 0
      ? form.descendantsOfIds?.map((d) => d.id)
      : null;
  filters.projectIds =
    Array.isArray(form.projectIds) && form.projectIds.length > 0 ? form.projectIds?.map((d) => d.id) : null;
  return filters;
};
/**
 * Converts InventoryItemFilters to InventoryFilterForm
 * @param filters InventoryItemFilters
 * @returns InventoryFilterForm
 * @author @CorradoSurmanowicz
 */
export const inventoryItemsFiltersToForm = (filters: InventoryItemFilters): InventoryItemFilterForm => {
  return {
    customTypeIds: filters.customTypeIds?.map((id) => ({ id, name: "" })) || null,
    childrenOfParentIds: filters.childrenOfParentIds?.map((id) => ({ id, name: "" })) || null,
    descendantsOfIds: filters.descendantsOfIds?.map((id) => ({ id, name: "" })) || null,
    projectIds: filters.projectIds?.map((id) => ({ id, name: "" })) || null,
    ...combinedModificationRecordEntityFiltersToForm(filters),
    ...lockableEntityFiltersToForm(filters),
    ...signableEntityFiltersToForm(filters),
    ...softDeletableEntityFiltersToForm(filters),
  };
};

interface InventoryItemsFilterBarProps
  extends TabStoreFilterSidebarProps<InventoryItem, InventoryItemFilterForm, InventoryItemFilters> {
  hideHierarchyFilters?: boolean;
}

export const InventoryItemsFilterBar = ({
  initialValues,
  dispatchTabStore,
  onFormChange,
  tabsLoading,
  currentTab,
  hideHierarchyFilters = false,
}: InventoryItemsFilterBarProps) => {
  // const stateArray = [
  //   !!initialValues?.customTypeIds?.length,
  //   !!initialValues?.childrenOfParentIds?.length,
  //   !!initialValues?.descendantsOfIds?.length,
  //   !!initialValues?.projectIds?.length,
  // ];

  return (
    <TabStoreFilterFormProvider
      tabsLoading={tabsLoading}
      initialValues={initialValues}
      defaultValues={defaultValues}
      defaultFilters={defaultInventoryItemsFilters}
      formToFilters={inventoryItemsFormToFilters}
      onFormChange={onFormChange}
      dispatchTabStore={dispatchTabStore}
      customTypeConstraint="InventoryItem"
    >
      {({ control, errors, onReset, watch, customFieldsDict }) => {
        const currentCustomTypeIds = watch("customTypeIds")?.map((c) => c.id);

        return (
          <EntityFilterSideBar onReset={onReset}>
            <FoldableFilters
              label="General"
              // defaultOpen={stateArray.some((p) => p) && !!stateArray.length}
              defaultOpen
            >
              {!hideHierarchyFilters && (
                <>
                  <CustomTypesVirtualizedSelectForm
                    id="customTypeIds"
                    label="Inventory types"
                    control={control}
                    isMulti
                    filters={{ entityTypes: ["InventoryItem"] }}
                    placeholder="Select inventory types..."
                    enableBasicOption
                  />

                  <InventoryItemsVirtualizedSelectForm
                    id="childrenOfParentIds"
                    label="Direct children of"
                    control={control}
                    isMulti
                    filters={{ customTypeIds: !!currentCustomTypeIds?.length ? currentCustomTypeIds : undefined }}
                  />
                  <InventoryItemsVirtualizedSelectForm
                    id="descendantsOfIds"
                    label="All descendants of"
                    control={control}
                    isMulti
                    filters={{ customTypeIds: !!currentCustomTypeIds?.length ? currentCustomTypeIds : undefined }}
                  />
                </>
              )}
              <ProjectsVirtualizedSelectForm id="projectIds" label="Project" control={control} isMulti />
            </FoldableFilters>
            {customFieldsDict && (
              <CustomFieldFilterLayout
                control={control}
                errors={errors}
                customFieldsDict={customFieldsDict}
                entityTypes={["InventoryItem"]}
              />
            )}

            <CombinedModificationRecordEntityFilterForm
              control={control}
              currentTab={currentTab}
              initialValues={initialValues}
            />

            <SignableEntityFilterForm control={control} initialValues={initialValues} />

            {/* <SoftDeletableEntityFilterForm control={control} currentTab={currentTab} initialValues={initialValues} /> */}
          </EntityFilterSideBar>
        );
      }}
    </TabStoreFilterFormProvider>
  );
};
